import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  createTelegramAuthLink,
  getTelegramAuthStatus,
  loginUser,
} from '../../requests/login/login';
import { getApiErrorMessage } from '../../requests/apiClient';

interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  isTelegramAuthLoading: boolean;
  refreshSession: () => Promise<void>;
  startTelegramAuth: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextValue>({
  isAuthenticated: false,
  isLoading: true,
  isTelegramAuthLoading: false,
  refreshSession: async () => {},
  startTelegramAuth: async () => false,
});

const AUTH_POLL_INTERVAL_MS = 2500;
const AUTH_POLL_TIMEOUT_MS = 120000;
const AUTH_FIRST_CHECK_DELAY_MS = 2000;

export const useAuth = () => useContext(AuthContext);

const wait = (timeout: number) =>
  new Promise<void>((resolve) => {
    window.setTimeout(resolve, timeout);
  });

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isTelegramAuthLoading, setIsTelegramAuthLoading] = useState(false);

  const refreshSession = useCallback(async () => {
    try {
      const response = await loginUser();
      setIsAuthenticated(Boolean(response?.data?.authenticated));
    } catch (error) {
      console.error('Session init failed:', error);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshSession();
  }, [refreshSession]);

  const startTelegramAuth = useCallback(async (): Promise<boolean> => {
    if (isTelegramAuthLoading) {
      return false;
    }

    setIsTelegramAuthLoading(true);
    let popup: Window | null = null;
    try {
      popup = window.open('', '_blank');
      if (!popup) {
        throw new Error('PopupBlocked');
      }

      const linkResponse = await createTelegramAuthLink();
      const code = linkResponse?.data?.code;
      const url = linkResponse?.data?.url;

      if (!code || !url) {
        throw new Error('Telegram auth link is missing');
      }

      popup.location.href = url;
      await wait(AUTH_FIRST_CHECK_DELAY_MS);

      const startedAt = Date.now();
      while (Date.now() - startedAt < AUTH_POLL_TIMEOUT_MS) {
        try {
          const statusResponse = await getTelegramAuthStatus(code);
          if (statusResponse?.data?.authenticated) {
            setIsAuthenticated(true);
            if (!popup.closed) {
              popup.close();
            }
            return true;
          }
        } catch (pollError) {
          const message = getApiErrorMessage(pollError).toLowerCase();
          const isRetryable =
            message.includes('invalid auth session') ||
            message.includes('pending') ||
            message.includes('forbidden');
          if (!isRetryable) {
            throw pollError;
          }
        }
        await wait(AUTH_POLL_INTERVAL_MS);
      }

      return false;
    } catch (error) {
      console.error('Telegram auth failed:', error);
      return false;
    } finally {
      setIsTelegramAuthLoading(false);
    }
  }, [isTelegramAuthLoading]);

  const value = useMemo(
    () => ({
      isAuthenticated,
      isLoading,
      isTelegramAuthLoading,
      refreshSession,
      startTelegramAuth,
    }),
    [
      isAuthenticated,
      isLoading,
      isTelegramAuthLoading,
      refreshSession,
      startTelegramAuth,
    ],
  );

  if (isLoading) {
    return null;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
